import { WidgetRuntimeConfigProvider } from "@/context/widget-runtime-config";
import { env } from "@/env";
import { useChatManager } from "@/hooks/use-chat-manager";
import { DISCLAIMER_TEXT } from "@/constants/disclaimer";
import { CHAT_PLACEHOLDER_TEXT } from "@/constants/search-constants";
import { resolvePlaceholder } from "@/utils/resolve-placeholder";
import { resolveSuggestedQuestions } from "@/utils/resolve-suggested-questions";
import { useModalState } from "@/store/modal-state";
import {
  applyThemeVariables,
  applyThemeWithOverrides,
  buildAttributeOverrides,
  fetchWidgetTheme,
  injectBaseStyles,
  readWidgetAttributes,
} from "@/utils/widget-runtime";
import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import type { FC } from "react";
import { useRef } from "react";
import ReactDOM from "react-dom/client";
import { Sentry } from "../../instrument";
import { ChatModal } from "../chat-components/chat-modal";

const ChatModalComponent: FC = () => {
  const { isModalOpen, closeModal } = useModalState();
  const chatManager = useChatManager();
  const container = useRef<HTMLDivElement>(null);

  return (
    <div ref={container}>
      <ChatModal
        isOpen={isModalOpen}
        onOpenChange={(open) => {
          if (!open) closeModal();
        }}
        chatManager={chatManager}
        container={container.current}
      />
    </div>
  );
};

class ChatModalWebComponent extends HTMLElement {
  private root: ReactDOM.Root | null = null;
  private isMounted = false;
  private abortController: AbortController | null = null;

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  connectedCallback() {
    this.isMounted = true;
    this.abortController?.abort();
    this.abortController = new AbortController();
    // Inject base styles including system fonts
    injectBaseStyles(this);

    const attributes = readWidgetAttributes(this);
    if (!attributes.apiKey) {
      console.error("Tenant UI Widget: api-key attribute is required.");
      return;
    }

    const apiUrl = attributes.apiUrl ?? env.API_URL;
    const runtimeConfig = {
      apiKey: attributes.apiKey,
      apiUrl,
      organizationSlug: undefined as string | undefined,
      widgetDisclaimerText: DISCLAIMER_TEXT,
      widgetSuggestedQuestions: resolveSuggestedQuestions(
        attributes.suggestedQuestions,
        undefined,
      ),
      showChatQuestions: true,
      widgetPlaceholderText: resolvePlaceholder(attributes.placeholderText, undefined, CHAT_PLACEHOLDER_TEXT),
      langfuse: null as { baseUrl: string; publicKey: string; environment?: string } | null,
    };

    const colorOverrides = buildAttributeOverrides(attributes);

    applyThemeVariables(this, colorOverrides, true);

    // Create a custom Emotion cache for this shadow root
    const cache = createCache({
      key: "shadow-emotion-modal",
      container: this.shadowRoot as unknown as Element,
    });

    void fetchWidgetTheme(apiUrl, attributes.apiKey, this.abortController.signal).then(({ theme, disclaimerText, suggestedQuestions, showChatQuestions, placeholderText, organizationSlug, langfuse }) => {
      if (!this.isMounted) {
        return;
      }
      applyThemeWithOverrides(this, theme, colorOverrides);
      runtimeConfig.organizationSlug = organizationSlug ?? runtimeConfig.organizationSlug;
      runtimeConfig.widgetDisclaimerText = disclaimerText ?? runtimeConfig.widgetDisclaimerText;
      runtimeConfig.widgetSuggestedQuestions = resolveSuggestedQuestions(
        attributes.suggestedQuestions,
        suggestedQuestions,
      );
      runtimeConfig.showChatQuestions = showChatQuestions;
      runtimeConfig.widgetPlaceholderText = resolvePlaceholder(
        attributes.placeholderText,
        placeholderText,
        CHAT_PLACEHOLDER_TEXT,
      );
      runtimeConfig.langfuse = langfuse ?? null;
      if (this.root) {
        this.root.render(
          <CacheProvider value={cache}>
            <WidgetRuntimeConfigProvider value={runtimeConfig}>
              <Sentry.ErrorBoundary
                beforeCapture={(scope) => {
                  scope.setTag("component", "tenant-ai-widget-chat-modal");
                  scope.setContext("widget", {
                    shadowDOM: true,
                    component: "ChatModalWebComponent",
                  });
                }}
              >
                <ChatModalComponent />
              </Sentry.ErrorBoundary>
            </WidgetRuntimeConfigProvider>
          </CacheProvider>,
        );
      }
    });

    this.root = ReactDOM.createRoot(this.shadowRoot as ShadowRoot);
    this.root.render(
      <CacheProvider value={cache}>
        <WidgetRuntimeConfigProvider value={runtimeConfig}>
          <Sentry.ErrorBoundary
            beforeCapture={(scope) => {
              scope.setTag("component", "tenant-ai-widget-chat-modal");
              scope.setContext("widget", {
                shadowDOM: true,
                component: "ChatModalWebComponent",
              });
            }}
          >
            <ChatModalComponent />
          </Sentry.ErrorBoundary>
        </WidgetRuntimeConfigProvider>
      </CacheProvider>,
    );
  }

  disconnectedCallback() {
    this.isMounted = false;
    this.abortController?.abort();
    this.abortController = null;
    if (this.root) {
      this.root.unmount();
      this.root = null;
    }
  }
}

export default ChatModalWebComponent;
